import { call, delay, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as guid } from 'uuid';
import { Loading } from '../../../../../base/enums';
import { appActions } from '../../../../../base/redux/slices/appSlice';
import { responsesApi } from '../../api';
import { responsesActions } from '../slices/responsesSlice';
import { ApiResponse } from '../../../../../base/api';

import { baseErrorHandler } from '../../../../../base/redux/sagas/appSaga';
import {
    GetResponsesCountersResponse,
    GetResponsesParams,
    HireStatusParams,
    ResponseItem,
    VacancyItem,
} from '../../api/interfaces';
import { RootState } from '../../../../../base/redux/store';
import { CanceledError } from 'axios';
import { HireStatus } from '../../enums';
import { PayloadAction } from '@reduxjs/toolkit';
import { dateTotimestampWithNoMilliseconds } from '../../../../../base/utils';
import { __t } from '../../../../../base/utils/t';
import { vacancyActions } from '../../../vacancy/redux/slices/vacancySlice';

function* fetchResponseTypeTabData() {
    yield put(appActions.setLoading(Loading.App));
    yield put(responsesActions.setResponses({ data: [] }));
    yield call(getResponsesCounters);
    yield call(getVacancyList);
    yield call(getResponses);
    yield put(appActions.setLoading(null));
}

function* getResponsesCounters() {
    try {
        const { data }: ApiResponse<GetResponsesCountersResponse> = yield call(responsesApi.getResponseCounters);
        yield put(responsesActions.setResponsesCounters(data));
    } catch (e) {
        yield call(baseErrorHandler, e);
    }
}

function* getVacancyList() {
    try {
        const { data }: ApiResponse<VacancyItem[]> = yield call(responsesApi.getVacancyList);
        yield put(responsesActions.setVacancyList(data));
    } catch (e) {
        yield call(baseErrorHandler, e);
    }
}

function* getResponsesDelayed() {
    yield delay(300);
    yield call(getResponses);
}

let getResponsesRequestController: AbortController | null = null;

function* getResponses() {
    try {
        if (getResponsesRequestController) {
            getResponsesRequestController.abort();
        }
        getResponsesRequestController = new AbortController();
        yield put(appActions.setLoading(Loading.App));
        const {
            type,
            filters: { vacancyId, hireStatus, dateFrom, dateTo },
            pagination: { perPage, currentPage },
            workerSearch,
        } = yield select((state: RootState) => state.employerResponses);
        const params: GetResponsesParams = {
            type,
            workerName: workerSearch ? workerSearch : undefined,
            vacancyId: vacancyId !== -1 ? vacancyId : undefined,
            hireStatus: hireStatus !== HireStatus.All ? hireStatus : undefined,
            dateFrom: dateFrom ? dateTotimestampWithNoMilliseconds(dateFrom) : undefined,
            dateTo: dateTo ? dateTotimestampWithNoMilliseconds(dateTo) : undefined,
            perPage,
            page: currentPage,
        };
        const { data, pageCount, totalCount }: ApiResponse<ResponseItem[]> = yield call(
            responsesApi.getResponses,
            params,
            getResponsesRequestController.signal,
        );
        yield put(responsesActions.setResponses({ data, pageCount, totalCount }));

        getResponsesRequestController = null;
        yield put(appActions.setLoading(null));
    } catch (e) {
        if (e instanceof CanceledError) {
            getResponsesRequestController = null;
            return;
        }

        yield call(baseErrorHandler, e);
        yield put(appActions.setLoading(null));
    }
}

function* setResponseStatus({
    payload: { responseId, hireStatus },
}: PayloadAction<{ responseId: number; hireStatus: HireStatusParams }>) {
    try {
        yield put(appActions.setLoading(Loading.App));
        yield call(responsesApi.setResponseStatus, responseId, hireStatus);
        yield put(responsesActions.setResponseStatusSuccess({ responseId, hireStatus }));
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* setResponseArchivationStatus({
    payload: { responseId, isArchived },
}: PayloadAction<{ responseId: number; isArchived: boolean }>) {
    try {
        yield put(appActions.setLoading(Loading.App));
        yield call(responsesApi.setResponseArchivationStatus, responseId, isArchived);
        yield put(appActions.setLoading(null));
        yield delay(500);
        const message = isArchived ? __t('Відгук переміщено в Архів') : __t('Відгук розархівовано');
        yield put(
            appActions.enqueueSnackbar({
                id: guid(),
                message,
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );

        if (isArchived) {
            yield put(responsesActions.setResponseArchivationStatusSuccess(responseId));
            yield delay(400);
            yield put(responsesActions.setHighlightArchivationIcon(true));
            yield delay(200);
        }

        yield put(responsesActions.removeResponseFromList(responseId));
    } catch (e) {
        yield call(baseErrorHandler, e);
        yield put(appActions.setLoading(null));
    }
}

function* getResponseHistoryByWorkerId({ payload: workerId }: PayloadAction<number>) {
    try {
        yield put(appActions.setLoading(Loading.ResponseHistory));
        const { data }: ApiResponse<ResponseItem[]> = yield call(responsesApi.getResponseHistoryByWorkerId, workerId);
        yield put(responsesActions.setResponseHistory(data));
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* markNewResponseAsRead({ payload: responseId }: PayloadAction<number>) {
    try {
        yield call(responsesApi.markNewReponseAsRead, responseId);
        yield put(responsesActions.markNewResponseAsReadSuccess(responseId));
    } catch (e) {
        yield call(baseErrorHandler, e);
    }
}

function* inviteWorkerForVacancySuccess({
    payload: { workerId },
}: PayloadAction<{ vacancyId: number; workerId: number }>) {
    yield call(getResponses);

    const { responseHistory } = yield select((state: RootState) => state.employerResponses);
    if (responseHistory.length) {
        yield put(responsesActions.getResponseHistoryByWorkerId(workerId));
    }
}

function* translateWorkerDescription({ payload: responseId }: PayloadAction<number>) {
    try {
        yield put(appActions.setLoading(Loading.App));
        const { data }: ApiResponse<ResponseItem> = yield call(responsesApi.translateResponse, responseId);
        yield put(
            responsesActions.setTranslationWorkerDescription({
                responseId,
                descriptionTranslated: data.worker.descriptionTranslated,
            }),
        );
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

export function* responsesSaga() {
    yield takeEvery(responsesActions.getResponsesCounters, getResponsesCounters);
    yield takeEvery(responsesActions.fetchResponseTypeTabData, fetchResponseTypeTabData);
    yield takeEvery(responsesActions.getResponses, getResponses);
    yield takeEvery(responsesActions.setFiltersVacancy, getResponses);
    yield takeEvery(responsesActions.setFiltersHireStatus, getResponses);
    yield takeEvery(responsesActions.setFiltersDates, getResponses);
    yield takeEvery(responsesActions.clearFilters, getResponses);
    yield takeEvery(vacancyActions.inviteWorkerForVacancySuccess, inviteWorkerForVacancySuccess);
    yield takeEvery(responsesActions.setWorkerSearch, getResponsesDelayed);
    yield takeEvery(responsesActions.getVacancyList, getVacancyList);
    yield takeEvery(responsesActions.setResponseStatusRequest, setResponseStatus);
    yield takeEvery(responsesActions.setResponseArchivationStatusRequest, setResponseArchivationStatus);
    yield takeEvery(responsesActions.getResponseHistoryByWorkerId, getResponseHistoryByWorkerId);
    yield takeEvery(responsesActions.markNewResponseAsReadRequest, markNewResponseAsRead);
    yield takeEvery(responsesActions.translateWorkerDescription, translateWorkerDescription);
}
